import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Autocomplete,
  Avatar,
  Stack,
  Divider,
  Alert,
  Collapse,
  Paper,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Share,
  Send,
  Business as BusinessIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  ManageAccounts as ManageAccountsIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachMoney as AttachMoneyIcon,
  ContentCopy as ContentCopyIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import client from '../../../../API';
import { toast } from 'sonner';
import useAuth from '../../../../Hooks/use-auth';
import { parseLocalFloat } from '../../../../Utils/constants';

// ─── Company Search Autocomplete ──────────────────────────────────────────────
function CompanySearchField({ value, onChange, disabled }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCompanies = useCallback(async (query) => {
    if (!query || query.length < 2) { setOptions([]); return; }
    setLoading(true);
    try {
      const result = await client.companies.list({ limit: 500, offset: 0 });
      const arr = Array.isArray(result) ? result : result?.companies || [];
      setOptions(arr.filter((c) => c.name?.toLowerCase().includes(query.toLowerCase())).slice(0, 20));
    } catch { setOptions([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCompanies(inputValue), 300);
    return () => clearTimeout(t);
  }, [inputValue, fetchCompanies]);

  useEffect(() => { if (value?.name) setInputValue(value.name); }, [value]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      options={options}
      getOptionLabel={(o) => o?.name || ''}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters…' : 'No companies found'}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={option.phphref || ''} alt={option.name}
            sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: 13, flexShrink: 0 }}>
            {option.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
            {option.account_status && (
              <Typography variant="caption" color="text.secondary">
                {option.account_status}{option.blitzpay_account ? ' · BlitzPay' : ''}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Client Company"
          placeholder="Search companies…"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (<><SearchIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />{params.InputProps.startAdornment}</>),
            endAdornment: (<>{loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
          }}
        />
      )}
    />
  );
}

// ─── Manager Select ───────────────────────────────────────────────────────────
function ManagerSelectField({ value, onChange, disabled, managers, loading }) {
  const selectedIndex = value
    ? managers.findIndex((m) => m.id === value.id || m.email === value.email)
    : '';
  const currentIndex = selectedIndex === -1 ? '' : selectedIndex;

  const handleChange = (e) => {
    const idx = e.target.value;
    if (idx === '') { onChange(null); return; }
    onChange(managers[idx] ?? null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Loading team members…</Typography>
      </Box>
    );
  }

  if (!managers.length) {
    return (
      <Alert severity="info" sx={{ py: 0.5, fontSize: '0.8rem' }}>
        No team members found.
      </Alert>
    );
  }

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>New Manager</InputLabel>
      <Select
        value={currentIndex}
        onChange={handleChange}
        label="New Manager"
        renderValue={(idx) => {
          if (idx === '') return <Typography color="text.secondary" variant="body2">Select a team member…</Typography>;
          const m = managers[idx];
          if (!m) return null;
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: 'primary.main' }}>
                {m.first_name?.charAt(0)}{m.last_name?.charAt(0)}
              </Avatar>
              <Typography variant="body2" fontWeight={600}>{m.first_name} {m.last_name}</Typography>
              <Typography variant="caption" color="text.secondary">— {m.email}</Typography>
            </Stack>
          );
        }}
      >
        <MenuItem value="">
          <Typography color="text.secondary" variant="body2">— No change —</Typography>
        </MenuItem>
        {managers.map((manager, index) => (
          <MenuItem value={index} key={manager.id ?? index}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: 'primary.main' }}>
                {manager.first_name?.charAt(0)}{manager.last_name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>{manager.first_name} {manager.last_name}</Typography>
                <Typography variant="caption" color="text.secondary">{manager.email}</Typography>
              </Box>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// ─── History Section ──────────────────────────────────────────────────────────
function HistorySection({ campaignId, open }) {
  const theme = useTheme();

  const { data, isFetching } = useQuery(
    ['campaign', campaignId, 'history'],
    () => client.campaigns.history(campaignId),
    { enabled: open, refetchOnWindowFocus: false }
  );

  const history = Array.isArray(data) ? data : data?.history || [];

  const flattenObject = (obj, parentKey = '', result = {}) => {
    for (let key in obj) {
      const propName = parentKey ? `${parentKey}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        flattenObject(obj[key], propName, result);
      } else {
        result[propName] = obj[key];
      }
    }
    return result;
  };

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!history.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1, pl: 0.5 }}>
        No history found for this campaign.
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
      {history.map((historyItem) => {
        let changes = historyItem.changes;
        if (typeof changes === 'string') {
          try { changes = JSON.parse(changes); } catch { changes = {}; }
        }
        const flattenedChanges = flattenObject(changes || {});

        return (
          <Paper
            key={historyItem.id}
            elevation={0}
            sx={{
              p: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {historyItem.timestamp ? new Date(historyItem.timestamp).toLocaleString() : '—'}
                </Typography>
                <Chip
                  label={historyItem.user_name || '—'}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              </Stack>

              <Divider />

              {Object.keys(flattenedChanges).length === 0 ? (
                <Typography variant="caption" color="text.secondary">No change details available.</Typography>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Changes
                  </Typography>
                  {Object.entries(flattenedChanges).map(([fieldName, change]) => {
                    let oldValue = '';
                    let newValue = '';

                    if (change && typeof change === 'object' && 'old_value' in change && 'new_value' in change) {
                      oldValue = change.old_value;
                      newValue = change.new_value;
                    } else {
                      oldValue = 'N/A';
                      newValue = change;
                    }

                    return (
                      <Box
                        key={fieldName}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.background.default, 0.6),
                          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight={700}
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.75 }}
                        >
                          {fieldName}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Old Value</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {oldValue !== null && oldValue !== undefined ? String(oldValue) : 'null'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>New Value</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {newValue !== null && newValue !== undefined ? String(newValue) : 'null'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

// ─── Agency Rate Section ──────────────────────────────────────────────────────
function AgencyRateSection({ campaignDetails }) {
  const creators = campaignDetails?.creators || [];
  const totalBilled = creators.reduce((sum, c) => sum + parseLocalFloat(c.price || 0), 0);
  const totalCreatorCost = creators.reduce((sum, c) => sum + parseLocalFloat(c.agencyRate || 0), 0);
  const margin = totalBilled > 0 ? ((totalBilled - totalCreatorCost) / totalBilled) * 100 : 0;

  const rows = [
    { label: 'Total Agency Rate (billed)', value: totalBilled, highlight: true },
    { label: 'Total Creator Cost (net)', value: totalCreatorCost, highlight: false },
    { label: 'Gross Margin', value: null, extra: `${margin >= 0 ? '+' : ''}${margin.toFixed(1)}%`, highlight: false, isMargin: true, positive: margin >= 0 },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(`$${totalBilled.toFixed(2)}`);
    toast.success('Agency rate copied!');
  };

  return (
    <Box sx={{ mt: 1 }}>
      {rows.map((row) => (
        <Box
          key={row.label}
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            py: 0.75, px: 1.25, mb: 0.5, borderRadius: 1.5,
            bgcolor: row.highlight ? 'primary.50' : 'grey.50',
            border: '1px solid',
            borderColor: row.highlight ? 'primary.100' : 'divider',
          }}
        >
          <Typography variant="body2" color={row.highlight ? 'primary.main' : 'text.secondary'} fontWeight={row.highlight ? 700 : 400}>
            {row.label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {row.isMargin ? (
              <Typography variant="body2" fontWeight={700} color={row.positive ? 'success.main' : 'error.main'}>
                {row.extra}
              </Typography>
            ) : (
              <Typography variant="body2" fontWeight={row.highlight ? 700 : 500}>
                ${row.value.toFixed(2)}
              </Typography>
            )}
            {row.highlight && (
              <Tooltip title="Copy" arrow>
                <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.25 }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}

      {creators.length > 0 && (
        <Box sx={{ mt: 1.5, maxHeight: 160, overflowY: 'auto' }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Per Creator
          </Typography>
          {creators.map((c) => (
            <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '60%' }}>{c.name}</Typography>
              <Typography variant="caption" fontWeight={600}>${parseLocalFloat(c.price || 0).toFixed(2)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Pricing Visibility Section ───────────────────────────────────────────────
function PricingVisibilitySection({ campaignDetails, queryClient }) {
  const theme = useTheme();
  const [hidePricing, setHidePricing] = useState(campaignDetails?.hide_public_pricing || false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync with campaign details when they change
  useEffect(() => {
    setHidePricing(campaignDetails?.hide_public_pricing || false);
  }, [campaignDetails?.hide_public_pricing]);

  const handleToggle = async (event) => {
    const newValue = event.target.checked;
    setIsUpdating(true);
    try {
      await client.campaigns.updatePricingVisibility(campaignDetails.id, newValue);
      setHidePricing(newValue);
      queryClient.invalidateQueries(['campaign', campaignDetails?.id]);
      queryClient.invalidateQueries(['campaign', campaignDetails?.id, 'details']);
      toast.success(`Public pricing ${newValue ? 'hidden' : 'visible'} on campaign page`);
    } catch (error) {
      console.error('Error updating pricing visibility:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to update pricing visibility');
      // Revert on error
      setHidePricing(!newValue);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: hidePricing
          ? alpha(theme.palette.warning.main, 0.3)
          : alpha(theme.palette.success.main, 0.25),
        bgcolor: hidePricing
          ? alpha(theme.palette.warning.main, 0.04)
          : alpha(theme.palette.success.main, 0.04),
        transition: 'all 0.2s ease',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: hidePricing
              ? alpha(theme.palette.warning.main, 0.12)
              : alpha(theme.palette.success.main, 0.12),
          }}
        >
          {hidePricing
            ? <VisibilityOffIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            : <VisibilityIcon sx={{ fontSize: 18, color: 'success.main' }} />
          }
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {hidePricing ? 'Pricing Hidden' : 'Pricing Visible'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hidePricing
              ? 'Creator prices are hidden from the public campaign page'
              : 'Creator prices are visible on the public campaign page'}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        {isUpdating && <CircularProgress size={14} />}
        <Switch
          checked={hidePricing}
          onChange={handleToggle}
          disabled={isUpdating}
          size="small"
          color={hidePricing ? 'warning' : 'success'}
        />
      </Stack>
    </Box>
  );
}

// ─── Main Dialog ──────────────────────────────────────────────────────────────
const ShareCampaignDialog = ({ open, onClose, campaignDetails, email, setEmail }) => {
  const queryClient = useQueryClient();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const isAdmin = (currentUser?.type || '').toLowerCase() === 'admin';

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySaved, setCompanySaved] = useState(false);

  const [selectedManager, setSelectedManager] = useState(null);
  const [managerSaved, setManagerSaved] = useState(false);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  // Fetch team members when dialog opens (admin only)
  useEffect(() => {
    if (!open || !isAdmin) return;
    setManagersLoading(true);
    client.companies
      .listUsers()
      .then((data) => setManagers(Array.isArray(data) ? data : []))
      .catch(() => setManagers([]))
      .finally(() => setManagersLoading(false));
  }, [open, isAdmin]);

  useEffect(() => {
    if (!open) return;
    setCompanySaved(false);
    setManagerSaved(false);
    setHistoryOpen(false);
    setRateOpen(false);
    setPricingOpen(false);
    setSelectedManager(null);

    if (campaignDetails?.client_company && typeof campaignDetails.client_company === 'object') {
      setSelectedCompany(campaignDetails.client_company);
    } else {
      setSelectedCompany(null);
    }
  }, [open, campaignDetails]);

  // ── Email share ──────────────────────────────────────────────────────────
  const { mutate: campaignShare, isLoading: isSharing } = useMutation(
    (input) => client.campaigns.share(campaignDetails?.id, input),
    {
      onSuccess: () => {
        toast.success('Campaign shared successfully!');
        onClose();
        queryClient.invalidateQueries(['campaign', campaignDetails?.id]);
        queryClient.invalidateQueries(['campaign', campaignDetails?.id, 'details']);
      },
      onError: (err) => toast.error(err.response?.data?.error || err.message),
    }
  );

  // ── Client company update ────────────────────────────────────────────────
  const { mutate: updateClientCompany, isLoading: isSavingCompany } = useMutation(
    (payload) => client.campaigns.updateClientCompany(campaignDetails?.id, payload),
    {
      onSuccess: () => {
        setCompanySaved(true);
        toast.success('Client company updated!');
        queryClient.invalidateQueries(['campaign', campaignDetails?.id]);
        queryClient.invalidateQueries(['campaign', campaignDetails?.id, 'details']);
      },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to update client company'),
    }
  );

  // ── Change manager mutation ──────────────────────────────────────────────
  const { mutate: updateManager, isLoading: isSavingManager } = useMutation(
    (payload) => client.campaigns.updateManager(campaignDetails?.id, payload),
    {
      onSuccess: () => {
        setManagerSaved(true);
        toast.success('Campaign manager updated!');
        queryClient.invalidateQueries(['campaign', campaignDetails?.id]);
        queryClient.invalidateQueries(['campaign', campaignDetails?.id, 'details']);
      },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to update manager'),
    }
  );

  const validateEmail = (addr) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(addr).toLowerCase());

  const handleShare = () => {
    if (validateEmail(email)) {
      campaignShare({ email });
    } else {
      toast.error('Please enter a valid email address');
    }
  };

  const handleSaveCompany = () => {
    if (!selectedCompany) return;
    updateClientCompany({ client_company_id: selectedCompany.id });
  };

  const handleSaveManager = () => {
    if (!selectedManager) return;
    updateManager({
      manager_id: selectedManager.id,
      manager_email: selectedManager.email,
    });
  };

  const handlePublicCampaignClick = () => { window.location.href = `/campaigns/${campaignDetails?.id}`; };

  const currentCompanyId = campaignDetails?.client_company?.id ?? campaignDetails?.client_company_id ?? null;
  const companyChanged = selectedCompany?.id !== currentCompanyId;
  const canSaveCompany = selectedCompany && companyChanged && !isSavingCompany;
  const canSaveManager = selectedManager && !isSavingManager;

  const collapsibleButtonSx = (isOpen) => ({
    justifyContent: 'flex-start',
    color: 'text.primary',
    fontWeight: 700,
    fontSize: '0.875rem',
    textTransform: 'none',
    px: 1,
    py: 0.75,
    borderRadius: 1.5,
    bgcolor: isOpen ? 'action.hover' : 'transparent',
    '&:hover': { bgcolor: 'action.hover' },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">

      {/* ── Header ── */}
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Share fontSize="small" />
        Share Campaign
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', ml: 1 }}>
          {campaignDetails?.name}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>

        {/* ── ADMIN ONLY: Client Company ─────────────────────────────────────── */}
        {isAdmin && (
          <>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <BusinessIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>Client Company</Typography>
                <Chip label="Admin Only" size="small" color="secondary" sx={{ fontSize: '0.6rem', height: 18 }} />
                {companySaved && (
                  <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label="Saved" size="small" color="success"
                    sx={{ fontSize: '0.65rem', height: 20, ml: 'auto' }} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                  <CompanySearchField
                    value={selectedCompany}
                    onChange={(v) => { setSelectedCompany(v); setCompanySaved(false); }}
                    disabled={isSavingCompany}
                  />
                </Box>
                <Button variant="outlined" size="small" onClick={handleSaveCompany} disabled={!canSaveCompany}
                  sx={{ mt: 0.5, whiteSpace: 'nowrap', minWidth: 80, height: 40 }}>
                  {isSavingCompany ? <CircularProgress size={14} /> : 'Save'}
                </Button>
              </Stack>
              {selectedCompany && (
                <Box sx={{
                  mt: 1.5, p: 1.5, borderRadius: 2, border: '1px solid',
                  borderColor: companySaved ? 'success.light' : 'divider',
                  bgcolor: companySaved ? 'success.50' : 'grey.50',
                  display: 'flex', alignItems: 'center', gap: 1.5, transition: 'all 0.2s ease',
                }}>
                  <Avatar src={selectedCompany.phphref || ''} alt={selectedCompany.name}
                    sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                    {selectedCompany.name?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{selectedCompany.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {selectedCompany.id}{selectedCompany.blitzpay_account ? ' · BlitzPay Account' : ''}
                    </Typography>
                  </Box>
                  {selectedCompany.blitzpay_account && (
                    <Chip label="⚡ BlitzPay" size="small" color="error" sx={{ fontSize: '0.65rem' }} />
                  )}
                </Box>
              )}
              {!selectedCompany && (
                <Alert severity="info" sx={{ mt: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                  Assign a client company to link this campaign to the correct account.
                </Alert>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ── ADMIN ONLY: Change Manager ─────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <ManageAccountsIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>Campaign Manager</Typography>
                <Chip label="Admin Only" size="small" color="secondary" sx={{ fontSize: '0.6rem', height: 18 }} />
                {managerSaved && (
                  <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label="Saved" size="small" color="success"
                    sx={{ fontSize: '0.65rem', height: 20, ml: 'auto' }} />
                )}
              </Stack>

              {campaignDetails?.user_email && (
                <Box sx={{
                  mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75,
                  bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Current:</Typography>
                  <Typography variant="caption">{campaignDetails.user_email}</Typography>
                </Box>
              )}

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                  <ManagerSelectField
                    value={selectedManager}
                    onChange={(v) => { setSelectedManager(v); setManagerSaved(false); }}
                    disabled={isSavingManager}
                    managers={managers}
                    loading={managersLoading}
                  />
                </Box>
                <Button variant="outlined" size="small" onClick={handleSaveManager} disabled={!canSaveManager}
                  sx={{ mt: 0.5, whiteSpace: 'nowrap', minWidth: 80, height: 40 }}>
                  {isSavingManager ? <CircularProgress size={14} /> : 'Save'}
                </Button>
              </Stack>

              {selectedManager && (
                <Box sx={{
                  mt: 1.5, p: 1.5, borderRadius: 2, border: '1px solid',
                  borderColor: managerSaved ? 'success.light' : 'divider',
                  bgcolor: managerSaved ? 'success.50' : 'grey.50',
                  display: 'flex', alignItems: 'center', gap: 1.5, transition: 'all 0.2s ease',
                }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.main' }}>
                    {selectedManager.first_name?.charAt(0)}{selectedManager.last_name?.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedManager.first_name} {selectedManager.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{selectedManager.email}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {/* ── Campaign History (collapsible) ────────────────────────────────── */}
        <Box sx={{ mb: 2 }}>
          <Button
            fullWidth
            variant="text"
            onClick={() => setHistoryOpen((p) => !p)}
            startIcon={<HistoryIcon fontSize="small" />}
            endIcon={historyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={collapsibleButtonSx(historyOpen)}
          >
            Campaign History
          </Button>
          <Collapse in={historyOpen} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.5, px: 0.5 }}>
              <HistorySection campaignId={campaignDetails?.id} open={historyOpen} />
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Agency Rate Breakdown (collapsible) ──────────────────────────── */}
        <Box sx={{ mb: 2 }}>
          <Button
            fullWidth
            variant="text"
            onClick={() => setRateOpen((p) => !p)}
            startIcon={<AttachMoneyIcon fontSize="small" />}
            endIcon={rateOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={collapsibleButtonSx(rateOpen)}
          >
            Agency Rate Breakdown
          </Button>
          <Collapse in={rateOpen} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1, px: 0.5 }}>
              <AgencyRateSection campaignDetails={campaignDetails} />
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Public Pricing Visibility (collapsible) ───────────────────────── */}
        <Box sx={{ mb: 2 }}>
          <Button
            fullWidth
            variant="text"
            onClick={() => setPricingOpen((p) => !p)}
            startIcon={
              (campaignDetails?.hide_public_pricing)
                ? <VisibilityOffIcon fontSize="small" />
                : <VisibilityIcon fontSize="small" />
            }
            endIcon={pricingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={collapsibleButtonSx(pricingOpen)}
          >
            Public Pricing Visibility
            {/* Badge indicating current state */}
            <Chip
              label={campaignDetails?.hide_public_pricing ? 'Hidden' : 'Visible'}
              size="small"
              color={campaignDetails?.hide_public_pricing ? 'warning' : 'success'}
              sx={{ fontSize: '0.6rem', height: 18, ml: 1 }}
            />
          </Button>
          <Collapse in={pricingOpen} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.5, px: 0.5 }}>
              <PricingVisibilitySection
                campaignDetails={campaignDetails}
                queryClient={queryClient}
              />
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Email recipient ──────────────────────────────────────────────── */}
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          label="Recipient Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleShare()}
          sx={{ mb: 2 }}
          InputProps={{ endAdornment: isSharing && <CircularProgress size={20} /> }}
        />

        {/* ── Already shared with ─────────────────────────────────────────── */}
        {campaignDetails?.shared_with?.length > 0 && (
          <Card variant="outlined">
            <CardContent sx={{ pb: '12px !important' }}>
              <Typography variant="subtitle2" gutterBottom>Already shared with:</Typography>
              <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                {campaignDetails.shared_with.map((addr, index) => (
                  <Chip key={index} label={addr} size="small" sx={{ m: 0.5 }}
                    onDelete={() => toast.info('Unshare functionality', { description: 'Feature coming soon' })} />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={handlePublicCampaignClick} color="primary" disabled={isSharing}>
          Public Campaign Page
        </Button>
        <Box>
          <Button onClick={onClose} color="secondary" disabled={isSharing} sx={{ mr: 1 }}>Cancel</Button>
          <Button variant="contained" onClick={handleShare} startIcon={<Send />} disabled={!email || isSharing}>
            {isSharing ? 'Sharing…' : 'Share Campaign'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ShareCampaignDialog;